import React, { useCallback, useRef, useState } from "react";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import { IconButton, Menu, MenuItem, MenuList, Stack, Typography } from "@mui/material";
import { DatePickerStyled } from "components/forms";
import { RoleComponentGuard } from "components/guards";
import { ContextMenuOptionObjectType } from "components/popups/Context-menu/Options-collection";
import { IFormMetaDetail } from "store/types";

import { RoleName } from "../../../constants";
import { useFormHook } from "../Hooks/use-form-hook";

const menuPaperSx = {
  borderRadius: "8px",
  boxShadow:
    "0px 3px 3px -2px rgba(38,54,70,0.16), 0px 3px 4px 0px rgba(38,54,70,0.1), 0px 1px 8px 0px rgba(38,54,70,0.08)",
  minWidth: 200,
  py: "8px"
};

const menuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  px: "12px",
  py: "8px",
  fontSize: 16,
  color: "#071f35",
  minHeight: 40,
  "& .MuiSvgIcon-root": { fontSize: 20, color: "#496682", flexShrink: 0 },
  "&:hover": { backgroundColor: "#ecf1f8" }
};

export type FormActionsHook = typeof useFormHook;

export interface Props {
  row: IFormMetaDetail;
  refetchHandler: () => Promise<void>;
  useHook?: FormActionsHook;
}

export const FormActions: React.FC<Props> = ({ row, refetchHandler, useHook = useFormHook }) => {
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const [pendingDateHandler, setPendingDateHandler] = useState<ContextMenuOptionObjectType | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);

  const handleAction = (fn?: () => void) => {
    closeMenu();
    fn?.();
  };

  const handleDatePickerAction = (handler: ContextMenuOptionObjectType) => () => {
    closeMenu();
    setPendingDateHandler(handler);
    setDatePickerAnchor(menuTriggerRef.current);
  };

  const onDateSave = useCallback(
    async (date: string) => {
      if (pendingDateHandler) {
        await pendingDateHandler.action(date);
      }
      setDatePickerAnchor(null);
      setPendingDateHandler(null);
    },
    [pendingDateHandler]
  );

  const onDateCancel = useCallback(() => {
    setDatePickerAnchor(null);
    setPendingDateHandler(null);
  }, []);

  const { handleShare, handleDelete, handleRename, handleDuplicate, handleConvertToPeriodic, handleCancelPeriodic } =
    useHook({
      form: { id: row.id, title: row.title, is_private: row.is_private, kind: row.kind },
      refetchHandler
    });

  const menuOptions = [
    {
      title: "Share",
      icon: <PersonAddOutlinedIcon />,
      onClick: () => handleAction(() => handleShare?.()),
      hidden: row.is_private || !row.deal_id,
      disabled: !handleShare
    },
    {
      title: "Duplicate",
      icon: <ContentCopyOutlinedIcon />,
      onClick: () => {
        if (typeof handleDuplicate === "function") {
          handleAction(() => (handleDuplicate as () => Promise<void>)());
        } else if (handleDuplicate) {
          handleDatePickerAction(handleDuplicate)();
        }
      },
      hidden: false,
      disabled: !handleDuplicate
    },
    {
      title: row.kind === "recurring" ? "Cancel Recurring" : "Recurring Form",
      icon: <RepeatOutlinedIcon />,
      onClick:
        row.kind === "recurring"
          ? () => handleAction(() => handleCancelPeriodic?.())
          : () => {
              if (handleConvertToPeriodic) handleDatePickerAction(handleConvertToPeriodic)();
            },
      hidden: false,
      disabled: row.kind === "recurring" ? !handleCancelPeriodic : !handleConvertToPeriodic
    },
    {
      title: "Rename",
      icon: <EditOutlinedIcon />,
      onClick: () => handleRename?.(menuTriggerRef, closeMenu),
      hidden: false,
      disabled: !handleRename
    },
    {
      title: "Delete",
      icon: <DeleteOutlineOutlinedIcon />,
      onClick: () => handleAction(() => handleDelete?.()),
      hidden: false,
      disabled: !handleDelete
    }
  ];

  return (
    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
      <>
        <Stack
          onClick={(e) => e.stopPropagation()}
          direction="row"
          className="row-hover-actions"
          justifyContent="flex-end"
          sx={{ opacity: 0, transition: "opacity 0.1s" }}
        >
          <IconButton
            ref={menuTriggerRef}
            size="sm"
            onClick={openMenu}
            aria-expanded={Boolean(menuAnchor)}
            sx={{ p: "4px", color: "#071f35", ...(menuAnchor && { opacity: 0.7 }) }}
          >
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: menuPaperSx } }}
          MenuListProps={{ sx: { padding: 0 } }}
        >
          <MenuList disablePadding>
            {menuOptions
              .filter((option) => !option.hidden)
              .map((option) => (
                <MenuItem key={option.title} sx={menuItemSx} onClick={option.onClick} disabled={option.disabled}>
                  {option.icon}
                  <Typography sx={{ fontSize: 16, color: "#071f35" }}>{option.title}</Typography>
                </MenuItem>
              ))}
          </MenuList>
        </Menu>
        {datePickerAnchor && (
          <div onClick={(e) => e.stopPropagation()}>
            <DatePickerStyled
              anchorEl={datePickerAnchor}
              onSave={onDateSave}
              onCancelClick={onDateCancel}
              placement="right"
              offset={[0, 15]}
            />
          </div>
        )}
      </>
    </RoleComponentGuard>
  );
};
